import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Broom, Warning, CheckCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DeadEmailCleanup banner — shows count of prospects with bounced /
 * complained / DNC emails and gives the admin a one-click cleanup button
 * that pauses their sales sequences automatically.
 */
export default function DeadEmailCleanup({ onCleaned }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/b2b/dead-emails/preview`, { withCredentials: true });
      setPreview(res.data);
    } catch (e) {
      setPreview({ total: 0, prospects: [], by_reason: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPreview(); }, []);

  const runCleanup = async () => {
    if (!preview || preview.total === 0) return;
    if (!window.confirm(`Esta acción pausará la secuencia de ${preview.total} prospects con emails muertos (bounced/complained/DNC). ¿Continuar?`)) return;
    try {
      setCleaning(true);
      const res = await axios.post(`${API_URL}/api/b2b/dead-emails/cleanup`, {}, { withCredentials: true });
      window.alert(`✅ Limpieza completada. Se pausaron ${res.data.cleaned} prospects.`);
      await fetchPreview();
      if (onCleaned) onCleaned();
    } catch (e) {
      window.alert('❌ Error en la limpieza: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setCleaning(false);
    }
  };

  if (loading) return null;
  if (!preview || preview.total === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800"
           data-testid="dead-email-cleanup-clean">
        <CheckCircle size={18} weight="fill" className="text-emerald-600" />
        Tu lista de prospects está limpia: 0 emails muertos detectados.
      </div>
    );
  }

  const r = preview.by_reason || {};
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-300"
         data-testid="dead-email-cleanup-banner">
      <div className="flex items-start gap-3">
        <Warning size={22} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 leading-snug">
          <div className="font-bold">Emails muertos detectados: {preview.total} prospects</div>
          <div className="text-xs text-amber-800 mt-0.5">
            {r.hard_bounce ? <span className="mr-3">🚫 Hard bounce: <strong>{r.hard_bounce}</strong></span> : null}
            {r.spam_complaint ? <span className="mr-3">⚠️ Spam complaint: <strong>{r.spam_complaint}</strong></span> : null}
            {r.dnc ? <span className="mr-3">⛔ DNC: <strong>{r.dnc}</strong></span> : null}
            <span className="text-amber-700">— Estos prospects seguirán recibiendo emails inválidos hasta que los limpies.</span>
          </div>
        </div>
      </div>
      <button
        onClick={runCleanup}
        disabled={cleaning}
        data-testid="dead-email-cleanup-btn"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm"
      >
        <Broom size={18} weight="bold" />
        {cleaning ? 'Limpiando…' : 'Limpiar emails muertos'}
      </button>
    </div>
  );
}
